import { useState, useEffect, useCallback } from 'react';

/**
 * Custom hook for managing design tools state
 * Handles persistence, keyboard shortcuts, and tool state management
 */
const useDesignTools = () => {
    const STORAGE_KEY = 'layoutEditor_designTools';

    // Default state definition
    const defaultState = {
        isPanelOpen: false,
        sidebarPinned: true,
        darkMode: false,
        activeTab: 'view',
        grid: {
            enabled: false,
            size: 20,
            color: '#e2e8f0',
            opacity: 0.5,
            snapToGrid: false
        },
        rulers: {
            enabled: false,
            color: '#94a3b8',
            unit: 'px'
        },
        areaNumbers: {
            visible: true,
            position: 'center'
        },
        timeline: {
            visible: true
        },
        outlineMode: {
            enabled: false,
            showContent: true
        },
        alignmentGuides: {
            enabled: true,
            color: '#8b5cf6',
            opacity: 0.8
        },
        safeZones: {
            enabled: false,
            margin: 5,
            color: '#ef4444',
            opacity: 0.3
        },
        areaColors: {},
        areaOpacity: {},
        backgroundPattern: 'none',
        crosshair: {
            enabled: false,
            color: '#ef4444',
            width: 1
        },
        focusMode: {
            enabled: false,
            opacity: 0.15
        },
        magnet: {
            enabled: false,
            snapDistance: 10
        },
        grayscale: {
            enabled: false
        }
    };

    // Load initial state from localStorage with safe merge
    const loadState = () => {
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                // Deep merge defaultState with parsed state to ensure new keys exist
                const mergeDeep = (target, source) => {
                    const output = { ...target };
                    if (isObject(target) && isObject(source)) {
                        Object.keys(source).forEach(key => {
                            if (isObject(source[key])) {
                                if (!(key in target)) {
                                    Object.assign(output, { [key]: source[key] });
                                } else {
                                    output[key] = mergeDeep(target[key], source[key]);
                                }
                            } else {
                                Object.assign(output, { [key]: source[key] });
                            }
                        });
                    }
                    return output;
                };

                // Helper to check if item is object
                const isObject = (item) => {
                    return (item && typeof item === 'object' && !Array.isArray(item));
                };

                // Merge: start with defaultState, overwrite with parsed values where they exist
                // Ideally we want: if key exists in parsed, use it. If not, use default.
                // But simplified: spread default, then spread parsed? No, deep merge needed for nested objects.

                // Better approach: Take default, overlay parsed on top.
                // If parsed is missing "focusMode", default keeps it.
                // If parsed has "grid", we take parsed grid. But what if parsed grid is missing "snapToGrid"?

                const deepMerge = (defaults, saved) => {
                    const result = { ...defaults };
                    for (const key in saved) {
                        if (saved[key] !== undefined) {
                            if (isObject(defaults[key]) && isObject(saved[key])) {
                                result[key] = deepMerge(defaults[key], saved[key]);
                            } else {
                                result[key] = saved[key];
                            }
                        }
                    }
                    return result;
                };

                return deepMerge(defaultState, parsed);
            }
        } catch (error) {
            console.error('Error loading design tools state:', error);
        }
        return defaultState;
    };

    const [toolsState, setToolsState] = useState(loadState);
    const [selectedTool, setSelectedTool] = useState(null);
    const [showTutorial, setShowTutorial] = useState(false);

    // Save state to localStorage whenever it changes
    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(toolsState));
        } catch (error) {
            console.error('Error saving design tools state:', error);
        }
    }, [toolsState]);

    // Toggle panel
    const togglePanel = useCallback(() => {
        setToolsState(prev => ({
            ...prev,
            isPanelOpen: !prev.isPanelOpen
        }));
    }, []);

    // Set active tab
    const setActiveTab = useCallback((tab) => {
        setToolsState(prev => ({
            ...prev,
            activeTab: tab
        }));
    }, []);

    // Update specific tool setting
    const updateTool = useCallback((toolName, settings) => {
        setToolsState(prev => {
            const current = prev[toolName];
            // If current value or new settings is not an object (or is null), replace directly
            if (typeof current !== 'object' || current === null || typeof settings !== 'object' || settings === null) {
                return {
                    ...prev,
                    [toolName]: settings
                };
            }
            // Otherwise merge
            return {
                ...prev,
                [toolName]: {
                    ...current,
                    ...settings
                }
            };
        });
    }, []);

    // Toggle tool enabled state
    const toggleTool = useCallback((toolName) => {
        setToolsState(prev => ({
            ...prev,
            [toolName]: {
                ...prev[toolName],
                enabled: !prev[toolName].enabled
            }
        }));
    }, []);

    // Set area color
    const setAreaColor = useCallback((areaId, color) => {
        setToolsState(prev => ({
            ...prev,
            areaColors: {
                ...prev.areaColors,
                [areaId]: color
            }
        }));
    }, []);

    // Set area opacity
    const setAreaOpacity = useCallback((areaId, opacity) => {
        setToolsState(prev => ({
            ...prev,
            areaOpacity: {
                ...prev.areaOpacity,
                [areaId]: opacity
            }
        }));
    }, []);

    // Show tutorial for specific tool
    const showToolTutorial = useCallback((toolName) => {
        setSelectedTool(toolName);
        setShowTutorial(true);
    }, []);

    // Hide tutorial
    const hideTutorial = useCallback(() => {
        setShowTutorial(false);
        setSelectedTool(null);
    }, []);

    // Reset all tools to default
    const resetTools = useCallback(() => {
        const defaultState = loadState();
        setToolsState({
            ...defaultState,
            isPanelOpen: toolsState.isPanelOpen
        });
    }, [toolsState.isPanelOpen]);

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyPress = (e) => {
            // Ctrl/Cmd + Shift + T: Toggle tools panel
            if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'T') {
                e.preventDefault();
                togglePanel();
            }
            // Ctrl/Cmd + Shift + G: Toggle grid
            if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'G') {
                e.preventDefault();
                toggleTool('grid');
            }
            // Ctrl/Cmd + Shift + R: Toggle rulers
            if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'R') {
                e.preventDefault();
                toggleTool('rulers');
            }
            // Ctrl/Cmd + Shift + O: Toggle outline mode
            if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'O') {
                e.preventDefault();
                toggleTool('outlineMode');
            }
            // Ctrl/Cmd + Shift + C: Toggle crosshair
            if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'C') {
                e.preventDefault();
                toggleTool('crosshair');
            }
            // Ctrl/Cmd + Shift + F: Toggle focus mode
            if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'F') {
                e.preventDefault();
                toggleTool('focusMode');
            }
            // Ctrl/Cmd + Shift + M: Toggle magnet
            if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'M') {
                e.preventDefault();
                toggleTool('magnet');
            }
            // Ctrl/Cmd + Shift + B: Toggle grayscale (B for Black & White)
            if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'B') {
                e.preventDefault();
                toggleTool('grayscale');
            }
        };

        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [togglePanel, toggleTool]);

    // Toggle sidebar pin
    const toggleSidebarPin = useCallback(() => {
        setToolsState(prev => ({
            ...prev,
            sidebarPinned: !prev.sidebarPinned
        }));
    }, []);

    // Toggle dark mode
    const toggleDarkMode = useCallback(() => {
        setToolsState(prev => ({
            ...prev,
            darkMode: !prev.darkMode
        }));
    }, []);

    return {
        toolsState,
        togglePanel,
        setActiveTab,
        updateTool,
        toggleTool,
        setAreaColor,
        setAreaOpacity,
        showToolTutorial,
        hideTutorial,
        resetTools,
        selectedTool,
        showTutorial,
        showTutorial,
        toggleSidebarPin,
        toggleDarkMode
    };
};

export default useDesignTools;
